// APP NAME
export enum AppName {
    /* Enumeration of application names for this project. */

    IceAI = 'ice-group',
    AutoAds = 'auto-ads',
    AniaMethod = 'ania-method',
    ClassicsAI = 'classics-ai',
}

// SUBSCRIPTION LEVEL
export type SubscriptionLevel =
    | 'FreeTier'
    | 'Basic'
    | 'BasicTier'
    | 'Enthusiast'
    | 'AdvancedTier'
    | 'Trader'
    | 'Unlimited'
    | 'Promotion'
    | 'Admin';
/* Represents the subscription levels stored on ia.user_subscription_levels.subscription_level. */

// REQUIRED TIER
export type RequiredTier = 'FreeTier' | 'BasicTier' | 'AdvancedTier' | 'Admin';
/* Represents menu and page access thresholds that compare against a user's subscription level. */

// APP GROUP NAMES
export const APP_GROUP_NAMES = [
    AppName.IceAI,
    AppName.AutoAds,
    AppName.AniaMethod,
    AppName.ClassicsAI,
] as const;
/* Cognito group names that grant access to each website app. */

// IS SELF SIGNUP ENABLED
export function isSelfSignupEnabled(appName: AppName): boolean {
    /* Returns false for ice-group where accounts are created by administrators only. */

    return appName !== AppName.IceAI;
}

// APP MENU ITEM
export interface AppMenuItem {
    /* Defines a menu item within an app config, omitting the app name which is inherited from the parent config. */

    name: string;
    path: string;
    isPublic: boolean;
    hideWhenDisabled: boolean;
    requiredTier: RequiredTier | null;
    componentType: 'server' | 'client';
    external?: boolean;
}

// MENU ITEM
export interface MenuItem extends AppMenuItem {
    /* Defines the structure of a menu item including its path, visibility, access requirements, and associated app. */

    appName: AppName;
}

// APP CONFIG
export interface AppConfig {
    /* Holds all per-app configuration including display name, icon, and menu items. */

    name: AppName;
    friendlyName: string;
    icon: string;
    menuItems: AppMenuItem[];
}

// APP CONFIGS
export const APP_CONFIGS = {
    [AppName.IceAI]: {
        name: AppName.IceAI,
        friendlyName: 'ice-ai',
        icon: '/images/ice-ai-icon.ico',
        menuItems: [
            {
                name: 'About',
                path: '/about',
                isPublic: true,
                hideWhenDisabled: false,
                requiredTier: null,
                componentType: 'server',
            },
            {
                name: 'Users',
                path: '/users',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'Admin',
                componentType: 'client',
            },
        ],
    },
    [AppName.AutoAds]: {
        name: AppName.AutoAds,
        friendlyName: 'Auto Ads',
        icon: '/images/auto-ads-icon.ico',
        menuItems: [
            {
                name: 'About',
                path: '/about',
                isPublic: true,
                hideWhenDisabled: false,
                requiredTier: null,
                componentType: 'server',
            },
            {
                name: 'Prospects',
                path: '/prospects',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'BasicTier',
                componentType: 'server',
            },
            {
                name: 'Search',
                path: '/search',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'BasicTier',
                componentType: 'server',
            },
            {
                name: 'Manual Entry',
                path: '/manual-entry',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'BasicTier',
                componentType: 'server',
            },
            {
                name: 'Resales',
                path: '/resales',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'BasicTier',
                componentType: 'server',
            },
            {
                name: 'House Sales',
                path: '/sale-items',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'BasicTier',
                componentType: 'server',
            },
        ],
    },
    [AppName.AniaMethod]: {
        name: AppName.AniaMethod,
        friendlyName: 'The Ania Method',
        icon: '/images/ania-method-icon.ico',
        menuItems: [
            {
                name: 'About',
                path: '/about',
                isPublic: true,
                hideWhenDisabled: false,
                requiredTier: null,
                componentType: 'server',
            },
            {
                name: 'Add Video',
                path: '/add-video',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'AdvancedTier',
                componentType: 'server',
            },
            {
                name: 'Preferences',
                path: '/preferences',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'AdvancedTier',
                componentType: 'server',
            },
            {
                name: 'Portfolio',
                path: '/portfolio',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: 'AdvancedTier',
                componentType: 'server',
            },
        ],
    },
    [AppName.ClassicsAI]: {
        name: AppName.ClassicsAI,
        friendlyName: 'Classics AI',
        icon: '/images/classics-ai-icon.ico',
        menuItems: [
            {
                name: 'Home',
                path: '/',
                isPublic: true,
                hideWhenDisabled: false,
                requiredTier: null,
                componentType: 'server',
            },
            {
                name: 'Search',
                path: '/search',
                isPublic: true,
                hideWhenDisabled: false,
                requiredTier: null,
                componentType: 'server',
            },
            {
                name: 'Forum',
                path: '/forum',
                isPublic: false,
                hideWhenDisabled: true,
                requiredTier: null,
                componentType: 'server',
            },
            {
                name: 'About',
                path: '/about',
                isPublic: true,
                hideWhenDisabled: false,
                requiredTier: null,
                componentType: 'server',
            },
        ],
    },
} satisfies Record<AppName, AppConfig>;
/* Contains all per-app configuration records for this project. */

// RESOLVE DEFAULT APP NAME
function resolveDefaultAppName(): AppName {
    /* Returns DEFAULT_APP_NAME when it matches a known app, otherwise Ice AI. */

    const fromEnv = process.env.DEFAULT_APP_NAME as AppName | undefined;
    if (fromEnv && fromEnv in APP_CONFIGS) {
        return fromEnv;
    }
    return AppName.IceAI;
}

// GET APP CONFIG
export function getAppConfig(appName: AppName): AppConfig {
    /* Returns the full configuration record for the specified app name. */

    return APP_CONFIGS[appName] ?? APP_CONFIGS[AppName.IceAI];
}

// TO MENU ITEMS
export function toMenuItems(appName: AppName, items: AppMenuItem[]): MenuItem[] {
    /* Stamps each app menu item with its parent app name to produce fully-qualified menu items. */

    return items.map((item) => ({
        ...item,
        appName,
    }));
}

// GET ALL MENU ITEMS
export function getAllMenuItems(): MenuItem[] {
    /* Flattens all menu items from every app config into a single list. */

    return Object.values(APP_CONFIGS).flatMap((config) =>
        toMenuItems(config.name, config.menuItems),
    );
}

// HOSTNAME APP ALIASES
const HOSTNAME_APP_ALIASES: Record<string, AppName> = {
    theaniamethod: AppName.AniaMethod,
    "anna-trainer": AppName.AniaMethod,
};
/* Second-level domains that do not equal the app slug but still serve The Ania Method. */

// GET APP NAME FROM HOSTNAME
export function getAppNameFromHostname(hostname: string): AppName {
    /* Returns the AppName that corresponds to a given hostname using the second-level domain segment. */

    // host headers include the port (localhost:3000); strip it before matching
    const host = hostname.split(",")[0].trim().replace(/:\d+$/, "");
    const parts = host.split(".");
    const secondLevelDomain = parts.length >= 2 ? parts[parts.length - 2] : host;
    const aliased = HOSTNAME_APP_ALIASES[secondLevelDomain];
    if (aliased) {
        return aliased;
    }
    const appName = Object.values(AppName).find((value) => value === secondLevelDomain);

    return appName || resolveDefaultAppName();
}

// GET APP NAME
export function getAppName(): AppName {
    /* Returns the AppName based on the current hostname, falling back to a default for server-side rendering. */

    if (typeof window === "undefined") {
        return resolveDefaultAppName();
    }

    return getAppNameFromHostname(window.location.hostname);
}
